(() => {
  const STORAGE_KEY = "smilodox-home-hero-utm-source";
  const FALLBACK_CHANNEL = "onlineshop";

  /**
   * Stable channel keys used by hero metaobjects and attribution.
   * Discount % may change (MAIL30 → MAIL40); stems stay fixed.
   */
  const CANONICAL_KEYS = new Set([
    "tt",
    "google",
    "meta",
    "fitx",
    "ff",
    "wa",
    "sms",
    "bm",
    "mail",
    "sale",
    "onlineshop",
  ]);

  /**
   * Prefixes that may appear as `{stem}{digits}` in ref / discount / path
   * (e.g. MAIL40, bm30, wa40, SALE45). Digits are stripped before matching.
   * `save` stems remap to `meta` (Meta ads creative) — see STEM_TO_CHANNEL.
   */
  const STRIP_DIGIT_STEMS = new Set(["mail", "bm", "sms", "wa", "sale", "save"]);

  /** Stem remaps after digit strip (when stem is not itself a canonical key). */
  const STEM_TO_CHANNEL = {
    save: "meta",
  };

  /**
   * Exact aliases → stable channel (legacy refs, partner codes, path names).
   */
  const ALIAS_TO_CHANNEL = {
    // Paid / path names
    tiktok: "tt",
    go: "google",
    save: "meta",
    // Email legacy
    email: "mail",
    email40: "mail",
    mail40: "mail",
    mail30: "mail",
    // Membership / SMS legacy (stem-strip also covers *40 / *30)
    bm40: "bm",
    bm30: "bm",
    sms40: "sms",
    sms30: "sms",
    // WhatsApp: stable wa + legacy campaign slug
    wa40: "wa",
    wa30: "wa",
    zfsberkw: "wa",
    // FitX family → one creative
    fitx: "fitx",
    "fitx-spind": "fitx",
    "fitx-tv": "fitx",
    "fitx-kurs": "fitx",
    "fitx-app": "fitx",
    "fitx-nl": "fitx",
    fitxma25smldx5672: "fitx",
    // Fitness First
    fitnessfirst: "ff",
    ff30smldx4310: "ff",
  };

  /** Explicit path overrides when path segment is not enough alone. */
  const PATH_TO_CHANNEL = {
    "/discount/tiktok": "tt",
    "/discount/go": "google",
    "/discount/save": "meta",
    "/discount/fitx": "fitx",
    "/discount/fitx-spind": "fitx",
    "/discount/fitx-tv": "fitx",
    "/discount/fitx-kurs": "fitx",
    "/discount/fitx-app": "fitx",
    "/discount/fitx-nl": "fitx",
    "/discount/fitxma25smldx5672": "fitx",
    "/discount/ff30smldx4310": "ff",
    "/discount/wa40": "wa",
    "/discount/zfsberkw": "wa",
  };

  const normalize = (value) => (value || "").toString().trim().toLowerCase();

  const stripDigitStem = (key) => {
    const match = key.match(/^([a-z]+)(\d+)$/);
    if (!match) return "";
    const stem = match[1];
    return STRIP_DIGIT_STEMS.has(stem) ? stem : "";
  };

  const toCanonical = (value) => {
    const key = normalize(value);
    if (!key) return "";

    if (ALIAS_TO_CHANNEL[key]) return ALIAS_TO_CHANNEL[key];

    const stem = stripDigitStem(key);
    if (stem) {
      const fromStem = STEM_TO_CHANNEL[stem] || stem;
      if (CANONICAL_KEYS.has(fromStem)) return fromStem;
    }

    if (CANONICAL_KEYS.has(key)) return key;

    return "";
  };

  const safeUrl = (value) => {
    if (!value) return null;

    try {
      return new URL(value, window.location.origin);
    } catch (error) {
      return null;
    }
  };

  const isHomepage = () =>
    document.documentElement.dataset.shopifyPageType === "index";

  const resolveFromUrl = (url) => {
    if (!url) return "";

    const ref = toCanonical(url.searchParams.get("ref"));
    if (ref) return ref;

    const utmSource = toCanonical(url.searchParams.get("utm_source"));
    if (utmSource) return utmSource;

    const discountParam = toCanonical(url.searchParams.get("discount"));
    if (discountParam) return discountParam;

    const normalizedPath = normalize(url.pathname.replace(/\/+$/, ""));
    if (PATH_TO_CHANNEL[normalizedPath]) return PATH_TO_CHANNEL[normalizedPath];

    // /discount/MAIL40 → strip/alias via toCanonical("mail40")
    const discountPathMatch = normalizedPath.match(/^\/discount\/([^/]+)$/);
    if (discountPathMatch) {
      return toCanonical(discountPathMatch[1]);
    }

    return "";
  };

  const readDiscountCookie = () => {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/i);
    if (!cookieMatch) return "";

    try {
      return normalize(decodeURIComponent(cookieMatch[1].replace(/\+/g, " ")));
    } catch (error) {
      return "";
    }
  };

  const resolveSaleChannel = () => {
    const fromUrl = resolveFromUrl(safeUrl(window.location.href));
    if (fromUrl) return fromUrl;

    if (isHomepage()) {
      const storedSession = toCanonical(sessionStorage.getItem(STORAGE_KEY));
      const fromCookie = toCanonical(readDiscountCookie());

      // First-touch /discount/* landings may only leave a cookie on the homepage.
      // Once a tagged session exists, untagged homepage visits fall back to onlineshop
      // so signup reflects current homepage context while PDP/cart-lock keep session.
      if (fromCookie && !storedSession) return fromCookie;

      return FALLBACK_CHANNEL;
    }

    const fromCookie = toCanonical(readDiscountCookie());
    if (fromCookie) return fromCookie;

    return toCanonical(sessionStorage.getItem(STORAGE_KEY)) || FALLBACK_CHANNEL;
  };

  const publishSaleChannel = (channel) => {
    const canonical = toCanonical(channel) || FALLBACK_CHANNEL;
    const isTaggedChannel = canonical !== FALLBACK_CHANNEL;
    const isHomepageFallback = isHomepage() && canonical === FALLBACK_CHANNEL;

    // Homepage fallback publishes onlineshop for current-page behavior only.
    // It must not clear a prior tagged session, because PDP/cart-lock may still
    // depend on that attribution from an earlier tagged landing visit.
    if (isTaggedChannel || !isHomepageFallback) {
      sessionStorage.setItem(STORAGE_KEY, canonical);
    }

    window.__smilodoxSaleChannel = canonical;
    window.__smilodoxHeroChannel = canonical;
    document.documentElement.dataset.saleChannel = canonical;
    document.documentElement.dataset.heroChannel = canonical;

    return canonical;
  };

  window.SmilodoxSaleChannel = {
    STORAGE_KEY,
    FALLBACK_CHANNEL,
    CANONICAL_KEYS,
    STRIP_DIGIT_STEMS,
    toCanonical,
    isHomepage,
    resolve: resolveSaleChannel,
    publish: publishSaleChannel,
    get: () =>
      toCanonical(window.__smilodoxSaleChannel) ||
      toCanonical(sessionStorage.getItem(STORAGE_KEY)) ||
      FALLBACK_CHANNEL,
  };

  publishSaleChannel(resolveSaleChannel());
})();
